// Macros: a reply template that also does something once the reply has gone out
// ("send the invoice reply, then resolve", "ask for the form, then remind me in 3 days").
//
// A macro lives on the template (reply_templates.after_send_action / _payload, migration
// 083). The composer remembers it when the template is inserted, shows it as a chip next to
// Send, and the outbox runs it only after the reply has actually been sent (never on Undo
// or on a failure). Everything it does goes through the usual paths: ticket actions through
// the dispatcher, reminders through set_ticket_reminder (CLAUDE.md §5).
//
// Pure on purpose: tests/macros.test.ts.

export type MacroAction =
  | 'resolve'
  | 'snooze_tomorrow'
  | 'snooze_2d'
  | 'snooze_monday'
  | 'set_priority'
  | 'reassign'
  | 'remind';

export interface AfterSend {
  action: MacroAction;
  payload: Record<string, unknown>;
}

/** What the template editor offers, in order. `needs` is the extra choice to ask for. */
export const MACRO_OPTIONS: ReadonlyArray<{
  action: MacroAction;
  label: string;
  needs?: 'priority' | 'member' | 'days';
}> = [
  { action: 'resolve', label: 'Resolve the ticket' },
  { action: 'snooze_tomorrow', label: 'Snooze until tomorrow 9am' },
  { action: 'snooze_2d', label: 'Snooze for 2 days' },
  { action: 'snooze_monday', label: 'Snooze until Monday 9am' },
  { action: 'set_priority', label: 'Set the priority…', needs: 'priority' },
  { action: 'reassign', label: 'Assign to…', needs: 'member' },
  { action: 'remind', label: 'Remind me to follow up…', needs: 'days' },
];

const PLAIN: ReadonlySet<string> = new Set(['resolve', 'snooze_tomorrow', 'snooze_2d', 'snooze_monday']);
const PRIORITIES: ReadonlySet<string> = new Set(['P1', 'P2', 'P3', 'P4']);

function isRecord(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

/** The macro stored on a template row, or null when there is none or it is not usable
 *  (an unknown action, a missing choice). Nothing here ever throws on bad data. */
export function parseAfterSend(action: string | null | undefined, payload: unknown): AfterSend | null {
  if (!action) return null;
  const p = isRecord(payload) ? payload : {};
  if (PLAIN.has(action)) return { action: action as MacroAction, payload: {} };
  if (action === 'set_priority') {
    return typeof p.priority === 'string' && PRIORITIES.has(p.priority)
      ? { action, payload: { priority: p.priority } }
      : null;
  }
  if (action === 'reassign') {
    return typeof p.reassign_to === 'string' && p.reassign_to
      ? { action, payload: { reassign_to: p.reassign_to } }
      : null;
  }
  if (action === 'remind') {
    const days = p.days === undefined ? 2 : p.days;
    return typeof days === 'number' && Number.isInteger(days) && days >= 1 && days <= 30
      ? { action, payload: { days } }
      : null;
  }
  return null;
}

export function describeAfterSend(a: AfterSend, memberName?: (slackId: string) => string | undefined): string {
  switch (a.action) {
    case 'resolve':
      return 'Resolve the ticket';
    case 'snooze_tomorrow':
      return 'Snooze until tomorrow 9am';
    case 'snooze_2d':
      return 'Snooze for 2 days';
    case 'snooze_monday':
      return 'Snooze until Monday 9am';
    case 'set_priority':
      return `Set priority to ${String(a.payload.priority)}`;
    case 'reassign': {
      const who = memberName?.(String(a.payload.reassign_to));
      return who ? `Assign to ${who}` : 'Assign to a teammate';
    }
    case 'remind': {
      const days = Number(a.payload.days);
      return days === 1 ? 'Remind me tomorrow' : `Remind me in ${days} days`;
    }
  }
}

export type MacroCall =
  | { kind: 'action'; action: string; payload: Record<string, unknown> | undefined }
  | { kind: 'reminder'; days: number };

/** What running the macro means: a dispatcher action, or a reminder. */
export function toTicketActionCall(a: AfterSend): MacroCall {
  if (a.action === 'remind') return { kind: 'reminder', days: Number(a.payload.days) };
  return { kind: 'action', action: a.action, payload: Object.keys(a.payload).length > 0 ? a.payload : undefined };
}

/** The permission (in the ticket's inbox) the macro needs; null when it needs none. */
export function afterSendPermission(a: AfterSend): 'tickets.status' | 'tickets.assign_others' | null {
  if (a.action === 'remind') return null;
  return a.action === 'reassign' ? 'tickets.assign_others' : 'tickets.status';
}

/** The two template columns for a macro (or for none). */
export function afterSendToColumns(a: AfterSend | null): {
  after_send_action: string | null;
  after_send_payload: Record<string, unknown>;
} {
  return a
    ? { after_send_action: a.action, after_send_payload: a.payload }
    : { after_send_action: null, after_send_payload: {} };
}

/** The template editor's "After sending" fields. One value per choice, so switching the action
 *  back and forth never loses what was picked for another. */
export interface MacroFormState {
  action: MacroAction | '';
  priority: string;
  member: string;
  days: number;
}

export const EMPTY_MACRO_FORM: MacroFormState = { action: '', priority: 'P2', member: '', days: 2 };

/** A template row's macro as editor fields. A macro that isn't usable shows as none. */
export function macroToForm(action: string | null | undefined, payload: unknown): MacroFormState {
  const a = parseAfterSend(action, payload);
  if (!a) return EMPTY_MACRO_FORM;
  return {
    action: a.action,
    priority: typeof a.payload.priority === 'string' ? a.payload.priority : EMPTY_MACRO_FORM.priority,
    member: typeof a.payload.reassign_to === 'string' ? a.payload.reassign_to : '',
    days: typeof a.payload.days === 'number' ? a.payload.days : EMPTY_MACRO_FORM.days,
  };
}

/** The macro the editor fields describe; null when none is picked or a choice is still missing. */
export function formToMacro(f: MacroFormState): AfterSend | null {
  if (!f.action) return null;
  const payload =
    f.action === 'set_priority'
      ? { priority: f.priority }
      : f.action === 'reassign'
        ? { reassign_to: f.member }
        : f.action === 'remind'
          ? { days: f.days }
          : {};
  return parseAfterSend(f.action, payload);
}
